/**
 * Members, Invitations & Access API client. Thin wrappers over the shared
 * `apiFetch` helper (see ./api) mapping to the Elysia /members and /invitations
 * modules.
 */
import type {
  AcceptInvitationInput,
  AssignRoleInput,
  InvitationDto,
  InvitationPreview,
  InviteInput,
  Member,
  MemberRoleAssignment,
  Paginated,
  RoleSummary,
} from '@fc/core'
import { apiFetch } from './api'

export type {
  AssignRoleInput,
  InvitationDto,
  InvitationPreview,
  InviteInput,
  Member,
  MemberRoleAssignment,
  RoleSummary,
  ScopeType,
} from '@fc/core'

export interface ScopeOptions {
  sites: { id: string; name: string }[]
  teams: { id: string; name: string }[]
  groups: { id: string; name: string }[]
}

export interface CreatedInvitation {
  invitation: InvitationDto
  /** One-time raw token — returned only once, copy it into the invite link. */
  token: string
}

// ---- Members ----
export const listMembers = async (): Promise<Member[]> => {
  const data = await apiFetch<Paginated<Member>>('/members')
  return data.items
}

export const listRoles = (): Promise<RoleSummary[]> => apiFetch<RoleSummary[]>('/members/roles')

export const listScopes = (): Promise<ScopeOptions> => apiFetch<ScopeOptions>('/members/scopes')

export const assignRole = (userId: string, input: AssignRoleInput): Promise<MemberRoleAssignment> =>
  apiFetch<MemberRoleAssignment>(`/members/${userId}/roles`, {
    method: 'POST',
    body: JSON.stringify(input),
  })

export const revokeRole = (userId: string, assignmentId: string): Promise<void> =>
  apiFetch<void>(`/members/${userId}/roles/${assignmentId}`, { method: 'DELETE' })

export const deactivateMember = (userId: string): Promise<{ ok: true }> =>
  apiFetch<{ ok: true }>(`/members/${userId}/deactivate`, { method: 'POST' })

export const reactivateMember = (userId: string): Promise<{ ok: true }> =>
  apiFetch<{ ok: true }>(`/members/${userId}/reactivate`, { method: 'POST' })

export const logoutEverywhere = (userId: string): Promise<{ ok: true }> =>
  apiFetch<{ ok: true }>(`/members/${userId}/logout-everywhere`, { method: 'POST' })

// ---- Invitations ----
export const listInvitations = async (): Promise<InvitationDto[]> => {
  const data = await apiFetch<Paginated<InvitationDto>>('/invitations')
  return data.items
}

export const createInvitation = (input: InviteInput): Promise<CreatedInvitation> =>
  apiFetch<CreatedInvitation>('/invitations', { method: 'POST', body: JSON.stringify(input) })

export const revokeInvitation = (id: string): Promise<void> =>
  apiFetch<void>(`/invitations/${id}`, { method: 'DELETE' })

// ---- Public (unauthenticated) ----
export const previewInvitation = (token: string): Promise<InvitationPreview> =>
  apiFetch<InvitationPreview>(`/invitations/preview?token=${encodeURIComponent(token)}`)

export const acceptInvitation = (input: AcceptInvitationInput): Promise<{ ok: true }> =>
  apiFetch<{ ok: true }>('/invitations/accept', {
    method: 'POST',
    body: JSON.stringify(input),
  })
